<template>
  <BaseModule
    v-bind="$props"
    class="container events-multi-module"
    data-module="events-multi"
  >
    <div
      v-if="hasBackgroundImage"
      class="bg"
      :style="{ backgroundImage: `url('${backgroundImage}')` }"
    ></div>
    <div class="content">
      <div class="text">
        <header
          v-if="title || subtitle"
          class="base-title-fontsize"
        >
          <component
            :is="titleTag"
            v-if="title"
            class="tit tit-g"
          >
            {{ title }}
          </component>
          <h3
            v-if="subtitle"
            class="sub"
            :style="{ color: subtitleColor }"
          >
            {{ subtitle }}
          </h3>
        </header>

        <div
          v-if="hasHoteis"
          class="lista-salas"
        >
          <div class="custom-scrollbar">
            <table class="tabela-hoteis">
              <thead class="tabela-hoteis__header">
                <tr class="tabela-hoteis__header__linha">
                  <th class="tabela-hoteis__header celula">Hotel</th>
                  <th class="tabela-hoteis__header celula">
                    {{ $t('downloadMaterial') }}
                  </th>
                </tr>
              </thead>
              <tbody class="tabela-hoteis__body c-2">
                <tr
                  v-for="(hotel, index) in getListaHoteis"
                  :key="index"
                  class="tabela-hoteis__body__linha"
                >
                  <td class="tabela-hoteis__body celula">
                    {{ hotel.nomeHotel }}
                  </td>
                  <td class="tabela-hoteis__body celula">
                    <a
                      v-if="hotel.material"
                      :href="hotel.material"
                      :style="{ color: iconColor }"
                      target="_blank"
                      class="c-1 download"
                    >
                      <IconDownload />
                    </a>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <EditorLink
          v-if="showButton(botaoTexto)"
          class="cta"
          :style="{
            backgroundColor: buttonColor,
            color: buttonTextColor,
          }"
          :model="botaoUrl"
        >
          {{ botaoTexto }}
        </EditorLink>
      </div>
    </div>
  </BaseModule>
</template>

<script>
import IconDownload from '@/components/icons/IconDownload.vue';
import showButton from '@/mixins/showButton';
import base from '@/mixins/base';
import EditorLink from './EditorLink.vue';
import BaseModule from './generic/BaseModule.vue';

export default {
  name: 'EventsMulti',
  components: {
    BaseModule,
    EditorLink,
    IconDownload,
  },
  mixins: [showButton, base],
  inheritAttrs: false,
  props: {
    ...BaseModule.props,
    backgroundImage: {
      type: String,
      default: '',
    },
    botaoTexto: {
      type: String,
      default: '',
    },
    botaoUrl: {
      type: Object,
      default: () => ({
        type: 'externalLink',
        value: '',
        openInNewTab: true,
      }),
    },
    buttonTextColor: {
      type: String,
      default: '',
    },
    subtitleColor: {
      type: String,
      default: '#333333',
    },
    iconColor: {
      type: String,
      default: '',
    },
    hoteis: {
      type: Array,
      default() {
        return [];
      },
    },
  },
  computed: {
    hasBackgroundImage() {
      return this.backgroundImage && /\S/.test(this.backgroundImage);
    },
    hasHoteis() {
      return this.hoteis && this.hoteis.length > 0;
    },
    getListaHoteis() {
      return this.hasHoteis ? this.hoteis : [];
    },
  },
  metadata: {
    extends: BaseModule.metadata,
    tabs: [
      {
        name: 'content',
        groups: [
          {
            name: 'hoteis',
            label: 'Hotéis',
            fields: [
              {
                name: 'hoteis',
                type: 'List',
                minItems: 1,
                itemLabel: 'Hotel {index}', // any {index} will be replaced by item's index (1-based)
                addItemLabel: 'Adicionar outro hotel',
                fields: [
                  {
                    name: 'nomeHotel',
                    type: 'ShortText',
                    label: 'Nome',
                    maxlength: 60,
                  },
                  {
                    name: 'material',
                    type: 'File',
                    label: 'Arquivo',
                    maxFileSize: 25,
                    helpText:
                      'São aceitos arquivos do tipo: PDF, Docx, JPG e PNG.',
                  },
                ],
              },
            ],
          },
          {
            name: 'back',
            label: 'Imagem de fundo',
            fields: [
              {
                name: 'backgroundImage',
                type: 'ImageFile',
                label: 'Imagem de fundo',
                enableRemoval: true,
                helpText:
                  'Dimensões recomendadas: 1920x680<br>Veja as especificações para a imagem <a href="https://docs.google.com/document/d/1BGNm5bLmKOETfTnxuCmasXX2cKQBbYBG4WxkM8Ky-kE/edit?usp=sharing" target="_blank">aqui</a>.',
              },
            ],
          },
          {
            name: 'button',
            label: 'Botão',
            fields: [
              {
                name: 'botaoTexto',
                type: 'ShortText',
                label: 'Texto do botão',
                maxlength: 25,
              },
              {
                name: 'botaoUrl',
                type: 'Url',
              },
            ],
          },
        ],
      },
      {
        name: 'appearance',
        groups: [
          {
            name: 'colors',
            fields: [
              {
                name: 'subtitleColor',
                after: 'titleColor',
                type: 'ColorPicker',
                label: 'Cor do subtítulo',
              },
              {
                name: 'iconColor',
                type: 'ColorPicker',
                label: 'Cor dos links da tabela',
              },
              {
                name: 'buttonTextColor',
                type: 'ColorPicker',
                label: 'Cor do texto do botão',
              },
            ],
            toRemove: ['textColor'],
          },
          {
            name: 'fonts',
            toRemove: ['contentSizeDesk', 'contentSizeMobile'],
          },
        ],
      },
    ],
  },
  i18n: {
    messages: {
      pt: {
        downloadMaterial: 'Baixe o material',
      },
      en: {
        downloadMaterial: 'Download the material',
      },
      es: {
        downloadMaterial: 'Descargar el material',
      },
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/styles/base/mixins.scss';

.events-multi-module {
  z-index: 0;
  padding-top: var(--padding-top);
  padding-bottom: var(--padding-bottom);

  .bg {
    position: absolute;
    top: 0;
    left: 0;
    opacity: 0.6;
  }

  .cta {
    width: fit-content;
    margin: auto;
    margin-top: 40px;
  }

  .lista-salas {
    background: $lb-white;
    margin: 0 auto;
    max-width: 780px;
    padding: 60px 30px 60px 60px;
    filter: drop-shadow(0px 10px 25px rgba(0, 0, 0, 0.1));

    .custom-scrollbar {
      max-height: 260px;
    }

    .tabela-hoteis {
      $T: &;
      width: 100%;

      &__header .celula {
        padding-bottom: 20px;
        font-size: $size-normal;
        font-style: normal;
        font-weight: $lb-semibold;
        line-height: 28px;
        letter-spacing: 0em;
        text-align: left;
      }

      &__body {
        &__linha:not(:first-of-type) {
          .celula {
            border-top: 1px solid $lb-light-gray;
          }
        }

        & .celula {
          line-height: 40px;
          text-align: left;
        }
      }

      &__header .celula,
      &__body .celula {
        &:last-of-type {
          text-align: center;
        }

        &:nth-child(1) {
          width: 75%;
        }

        &:nth-child(2) {
          width: 25%;
        }
      }
    }
  }

  @include media-breakpoint-down(md) {
    padding-top: var(--padding-top-mobile);
    padding-bottom: var(--padding-bottom-mobile);
  }

  @include media-breakpoint-down(sm) {
    .lista-salas {
      padding: res(30);
      padding-right: res(20);
    }

    .cta {
      width: 100%;
      margin-top: res(20);
    }
  }
}
</style>
